// token-utils is the ONE sanctioned place to import `classnames`: it wraps it
// into the configured `cn`. The latitude/* ban (CEXP-21461) sends everything
// else here instead.
// oxlint-disable-next-line no-restricted-imports
import classNames from 'classnames';
// oxlint-disable-next-line no-restricted-imports
import type { ArgumentArray } from 'classnames';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Validators for Anchorage design-system token scales, matched by NAMING
 * CONVENTION. Tokens that share a Tailwind class prefix with a different scale
 * (text-* = font-size|color, font-* = weight|family) are disambiguated by these
 * regexes. drift.test.ts asserts every token in the live @latitude/design-system
 * package matches its scale's validator — a token that breaks the convention
 * fails CI instead of silently escaping dedupe.
 */
export const tokenValidators = {
  // theme.spacing feeds p/m/gap/space/w/h/min-w/max-w/min-h/max-h/inset in twMerge
  spacing: (v: string) => /^(?:spacing|sizing)-(?:sys|comp|ref)-/.test(v),
  borderRadius: (v: string) => /^radius-(?:sys|comp|ref)-/.test(v),
  borderWidth: (v: string) => /^border-(?:sys|comp|ref)-/.test(v),
  opacity: (v: string) => /^opacity-(?:sys|ref)-/.test(v),
  boxShadow: (v: string) => v.startsWith('box-shadow-'),
  fontSize: (v: string) =>
    /^sys-size-\d/.test(v) || /^font-(?:ref|comp)-.+-font-size$/.test(v),
  lineHeight: (v: string) =>
    v.startsWith('sys-line-height-') ||
    /^font-(?:ref|comp)-.+-line-height$/.test(v),
  fontWeight: (v: string) =>
    v.startsWith('sys-weight-') || /^(?:ref|comp)-.+-font-weight$/.test(v),
  // Intentionally NOT wired into the twMerge config: the default font-family
  // group's isAny catch-all claims these after the extended font-weight group
  // claims weight tokens. Wiring it would change group-resolution order.
  // The validator exists to prove disjointness from fontWeight (drift.test.ts).
  fontFamily: (v: string) =>
    v.startsWith('sys-family-') || /^(?:ref|comp)-.+-font-family$/.test(v),
  letterSpacing: (v: string) =>
    /^font-(?:sys|ref|comp)-.*letter-spacing/.test(v),
};

const twMerge = extendTailwindMerge({
  // NOTE: twMerge keeps Tailwind's default `font-size` → clears `leading`
  // conflict (standard `text-base`/`text-lg` bundle a line-height and must reset
  // it). Anchorage decouples size/line-height into separate tokens, so when a
  // component sets BOTH an Anchorage `text-*-font-size` token and a `leading-*`
  // token in one cn() call, order them font-size-FIRST, leading-LAST — otherwise
  // the later font-size clears the earlier leading. (See DefaultLabelComponent.)
  extend: {
    theme: {
      spacing: [tokenValidators.spacing],
      borderRadius: [tokenValidators.borderRadius],
      borderWidth: [tokenValidators.borderWidth],
      opacity: [tokenValidators.opacity],
    },
    classGroups: {
      shadow: [{ shadow: [tokenValidators.boxShadow] }],
      leading: [{ leading: [tokenValidators.lineHeight] }],
      tracking: [{ tracking: [tokenValidators.letterSpacing] }],
      'font-size': [{ text: [tokenValidators.fontSize] }],
      'font-weight': [{ font: [tokenValidators.fontWeight] }],
    },
  },
});

/**
 * Canonical class-merge helper for @latitude/*. Resolves classnames shapes
 * (conditionals/arrays/objects), then dedupes Tailwind conflicts last-wins.
 * Pass the consumer `className` LAST so consumer overrides win without `!`.
 */
export const cn = (...inputs: ArgumentArray): string =>
  twMerge(classNames(inputs));
